import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Alert, AlertDescription, Badge, Button, Input, Label, Textarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { AlertCircle, Edit2, FileText, Save, Shield, Star, X } from 'lucide-react';
import { memo, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLcatHistory } from '../../../hooks/useLcatHistory';
import { usePortal } from '../../../hooks/usePortal';
import { useUpdateLCAT } from '../../../hooks/useUpdateLCAT';
import { EntityAuditTrail } from '../../common/EntityAuditTrail';
import { DocumentViewerDrawer } from '../DocumentViewer/DocumentViewerDrawer';
import { AmendmentTimeline } from '../v2/amendment/AmendmentTimeline';
const clearanceLabels = {
    none: 'None',
    public_trust: 'Public Trust',
    secret: 'Secret',
    top_secret: 'Top Secret',
    ts_sci: 'TS/SCI'
};
const clearanceColors = {
    none: 'bg-gray-100 text-gray-700',
    public_trust: 'bg-blue-100 text-blue-700',
    secret: 'bg-yellow-100 text-yellow-700',
    top_secret: 'bg-orange-100 text-orange-700',
    ts_sci: 'bg-red-100 text-red-700'
};
const educationLabels = {
    none: 'None',
    high_school: 'High School',
    associate: 'Associate',
    bachelor: "Bachelor's",
    master: "Master's",
    phd: 'PhD'
};
export const LCATDetailDrawer = /*#__PURE__*/ memo(function LCATDetailDrawer({ lcat, projectId, isOpen, onClose }) {
    const portalContainer = usePortal('lcat-detail-drawer-portal');
    const [isEditMode, setIsEditMode] = useState(false);
    const [formData, setFormData] = useState({
        title: lcat.title,
        description: lcat.description || '',
        category: lcat.category,
        level: lcat.level,
        estimatedHours: lcat.estimatedHours
    });
    const [isDocViewerOpen, setIsDocViewerOpen] = useState(false);
    const updateLCAT = useUpdateLCAT(projectId);
    // PR-C.4: pull the amendment-version chain for this role from the
    // project's full LCAT list. The hook reuses the existing useLCATs cache
    // and groups by logicalKey (set by the merge step in baltigo). Returns
    // an empty history when the role has only one version.
    const history = useLcatHistory(projectId, lcat);
    const timelineEntries = useMemo(()=>history.versions.map((row)=>({
                key: `${row.code ?? row.title}-${row.amendmentNumber ?? 'base'}-${row.amendmentSequence ?? 0}`,
                amendmentNumber: row.amendmentNumber,
                amendmentSequence: row.amendmentSequence,
                isCurrent: row.isCurrentVersion,
                sourceFilename: row.sourceFilename ?? row.source?.filename,
                primary: buildLcatPrimary(row),
                secondary: buildLcatSecondary(row)
            })), [
        history.versions
    ]);
    // Build source document reference from LCAT source data
    const sourceDocument = useMemo(()=>{
        const docId = lcat.source?.documentId || lcat.metadata?.sourceDocumentId;
        if (!docId) return null;
        return {
            documentId: docId,
            filename: lcat.source?.filename || 'Source Document',
            documentType: lcat.source?.documentType
        };
    }, [
        lcat.source,
        lcat.metadata?.sourceDocumentId
    ]);
    const handleSave = async ()=>{
        try {
            // formData already carries the user's edits and stays in sync
            // with the inputs via setFormData on each onChange — nothing to
            // re-seed here. The drawer's read-mode <p> reads lcat.* directly
            // (not formData), so refreshing read-mode after save relies on
            // the parent (LCATsTab) re-deriving `selectedLCAT` from the live
            // useLCATs cache when the post-save invalidation refetches.
            await updateLCAT.mutateAsync({
                lcatId: lcat.code,
                ...formData
            });
            setIsEditMode(false);
        } catch (error) {
            console.error('Failed to update LCAT:', error);
        }
    };
    const handleCancel = ()=>{
        setFormData({
            title: lcat.title,
            description: lcat.description || '',
            category: lcat.category,
            level: lcat.level,
            estimatedHours: lcat.estimatedHours
        });
        setIsEditMode(false);
    };
    const handleClose = ()=>{
        if (isEditMode) {
            handleCancel();
        }
        onClose();
    };
    // Get confidence badge
    const getConfidenceBadge = (confidence)=>{
        if (confidence == null) return /*#__PURE__*/ _jsx("span", {
            className: "text-gray-400",
            children: "N/A"
        });
        const percentage = (confidence * 100).toFixed(0);
        if (confidence >= 0.9) {
            return /*#__PURE__*/ _jsxs(Badge, {
                className: "bg-green-100 text-green-700",
                variant: "secondary",
                children: [
                    percentage,
                    "% (High Confidence)"
                ]
            });
        }
        if (confidence >= 0.7) {
            return /*#__PURE__*/ _jsxs(Badge, {
                className: "bg-yellow-100 text-yellow-700",
                variant: "secondary",
                children: [
                    percentage,
                    "% (Medium Confidence)"
                ]
            });
        }
        return /*#__PURE__*/ _jsxs(Badge, {
            className: "bg-orange-100 text-orange-700",
            variant: "secondary",
            children: [
                percentage,
                "% (Low Confidence)"
            ]
        });
    };
    if (!isOpen || !portalContainer) {
        return null;
    }
    const hasSourceDoc = lcat.source?.documentId || lcat.metadata?.sourceDocumentId;
    return /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity",
                onClick: handleClose
            }),
            /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.lcat.drawer,
                className: "fixed inset-y-0 right-0 w-full max-w-3xl bg-white shadow-xl z-50 flex flex-col",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "px-6 py-4 border-b border-gray-200 flex items-start justify-between",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx("h2", {
                                                className: "text-xl font-semibold",
                                                children: lcat.title
                                            }),
                                            lcat.isKeyPersonnel && /*#__PURE__*/ _jsxs(Badge, {
                                                className: "bg-purple-100 text-purple-700",
                                                variant: "secondary",
                                                children: [
                                                    /*#__PURE__*/ _jsx(Star, {
                                                        className: "w-3 h-3 mr-1"
                                                    }),
                                                    "Key Personnel"
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        className: "text-sm text-gray-500 mt-1",
                                        children: [
                                            "Labor Category: ",
                                            lcat.code
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2",
                                children: [
                                    isEditMode ? /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsxs(Button, {
                                                size: "sm",
                                                onClick: handleSave,
                                                disabled: updateLCAT.isPending,
                                                "data-testid": Testids.lcat.saveButton,
                                                children: [
                                                    /*#__PURE__*/ _jsx(Save, {
                                                        className: "w-4 h-4 mr-2"
                                                    }),
                                                    "Save"
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs(Button, {
                                                size: "sm",
                                                variant: "outline",
                                                onClick: handleCancel,
                                                children: [
                                                    /*#__PURE__*/ _jsx(X, {
                                                        className: "w-4 h-4 mr-2"
                                                    }),
                                                    "Cancel"
                                                ]
                                            })
                                        ]
                                    }) : /*#__PURE__*/ _jsxs(Button, {
                                        size: "sm",
                                        variant: "outline",
                                        onClick: ()=>setIsEditMode(true),
                                        "data-testid": Testids.lcat.editButton,
                                        children: [
                                            /*#__PURE__*/ _jsx(Edit2, {
                                                className: "w-4 h-4 mr-2"
                                            }),
                                            "Edit"
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Button, {
                                        size: "sm",
                                        variant: "ghost",
                                        onClick: handleClose,
                                        children: /*#__PURE__*/ _jsx(X, {
                                            className: "w-4 h-4"
                                        })
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex-1 overflow-y-auto px-6 py-6",
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-6",
                            children: [
                                updateLCAT.isError && /*#__PURE__*/ _jsxs(Alert, {
                                    variant: "destructive",
                                    children: [
                                        /*#__PURE__*/ _jsx(AlertCircle, {
                                            className: "h-4 w-4"
                                        }),
                                        /*#__PURE__*/ _jsxs(AlertDescription, {
                                            children: [
                                                "Failed to update labor category:",
                                                ' ',
                                                updateLCAT.error instanceof Error ? updateLCAT.error.message : 'Unknown error'
                                            ]
                                        })
                                    ]
                                }),
                                lcat.status && /*#__PURE__*/ _jsx(Badge, {
                                    variant: "secondary",
                                    className: lcat.status === 'confirmed' ? 'bg-green-100 text-green-800' : lcat.status === 'disputed' ? 'bg-red-100 text-red-800' : lcat.status === 'reviewed' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800',
                                    children: lcat.status
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Basic Information"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "grid grid-cols-2 gap-4",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "LCAT Code"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-sm text-gray-900 font-mono",
                                                            children: lcat.code
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Category (CLIN)"
                                                        }),
                                                        isEditMode ? /*#__PURE__*/ _jsx(Input, {
                                                            value: formData.category,
                                                            onChange: (e)=>setFormData({
                                                                    ...formData,
                                                                    category: e.target.value
                                                                }),
                                                            className: "mt-1"
                                                        }) : /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1",
                                                            children: lcat.category ? /*#__PURE__*/ _jsx(Badge, {
                                                                variant: "outline",
                                                                children: lcat.category
                                                            }) : /*#__PURE__*/ _jsx("span", {
                                                                className: "text-sm text-gray-400",
                                                                children: "Not specified"
                                                            })
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        lcat.teamName && /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx(Label, {
                                                    children: "Team"
                                                }),
                                                /*#__PURE__*/ _jsx("p", {
                                                    className: "mt-1",
                                                    children: /*#__PURE__*/ _jsx(Badge, {
                                                        className: "bg-indigo-100 text-indigo-700",
                                                        variant: "secondary",
                                                        children: lcat.teamName
                                                    })
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx(Label, {
                                                    htmlFor: "title",
                                                    children: "Title"
                                                }),
                                                isEditMode ? /*#__PURE__*/ _jsx(Input, {
                                                    id: "title",
                                                    value: formData.title,
                                                    onChange: (e)=>setFormData({
                                                            ...formData,
                                                            title: e.target.value
                                                        }),
                                                    className: "mt-1"
                                                }) : /*#__PURE__*/ _jsx("p", {
                                                    className: "mt-1 text-sm text-gray-900",
                                                    children: lcat.title
                                                })
                                            ]
                                        }),
                                        lcat.alternativeTitles && lcat.alternativeTitles.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx(Label, {
                                                    children: "Alternative Titles"
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "mt-1 flex flex-wrap gap-1",
                                                    children: lcat.alternativeTitles.map((alt, i)=>/*#__PURE__*/ _jsx(Badge, {
                                                            variant: "outline",
                                                            className: "text-xs",
                                                            children: alt
                                                        }, i))
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx(Label, {
                                                    htmlFor: "description",
                                                    children: "Description"
                                                }),
                                                isEditMode ? /*#__PURE__*/ _jsx(Textarea, {
                                                    id: "description",
                                                    value: formData.description,
                                                    onChange: (e)=>setFormData({
                                                            ...formData,
                                                            description: e.target.value
                                                        }),
                                                    rows: 4,
                                                    className: "mt-1"
                                                }) : /*#__PURE__*/ _jsx("p", {
                                                    className: "mt-1 text-sm text-gray-700 whitespace-pre-wrap",
                                                    children: lcat.description || 'No description available'
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                lcat.clinLinkages?.length ? /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-2 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "AI Reasoning by CLIN"
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "space-y-3",
                                            children: lcat.clinLinkages.map((cl)=>/*#__PURE__*/ _jsxs("div", {
                                                    className: "rounded-md bg-blue-50 border border-blue-200 p-3",
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex items-center gap-2 mb-1",
                                                            children: [
                                                                /*#__PURE__*/ _jsx(Badge, {
                                                                    variant: "outline",
                                                                    children: cl.clinNumber
                                                                }),
                                                                cl.confidence != null && /*#__PURE__*/ _jsxs("span", {
                                                                    className: "text-xs text-gray-500",
                                                                    children: [
                                                                        (cl.confidence * 100).toFixed(0),
                                                                        "% confidence"
                                                                    ]
                                                                })
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "text-sm text-blue-900 whitespace-pre-wrap",
                                                            children: cl.reasoning || 'No reasoning provided'
                                                        }),
                                                        cl.pwsReferences?.length || cl.attachmentReferences?.length ? /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex flex-wrap gap-1 mt-2",
                                                            children: [
                                                                cl.pwsReferences?.map((ref)=>/*#__PURE__*/ _jsxs(Badge, {
                                                                        className: "bg-purple-100 text-purple-700 text-xs",
                                                                        variant: "secondary",
                                                                        children: [
                                                                            "PWS ",
                                                                            ref
                                                                        ]
                                                                    }, ref)),
                                                                cl.attachmentReferences?.map((ref)=>/*#__PURE__*/ _jsx(Badge, {
                                                                        className: "bg-amber-100 text-amber-700 text-xs",
                                                                        variant: "secondary",
                                                                        children: ref
                                                                    }, ref))
                                                            ]
                                                        }) : null
                                                    ]
                                                }, cl.clinNumber))
                                        })
                                    ]
                                }) : lcat.reasoning ? /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-2 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "AI Reasoning"
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "bg-blue-50 border border-blue-200 rounded-md p-3",
                                            children: /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-gray-700 whitespace-pre-wrap",
                                                children: lcat.reasoning
                                            })
                                        })
                                    ]
                                }) : null,
                                (lcat.education || lcat.experience || lcat.clearance || lcat.certifications && lcat.certifications.length > 0) && /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-4 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Qualifications"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "grid grid-cols-2 gap-4",
                                            children: [
                                                lcat.education && /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Education"
                                                        }),
                                                        /*#__PURE__*/ _jsxs("p", {
                                                            className: "mt-1 text-sm text-gray-900",
                                                            children: [
                                                                educationLabels[lcat.education.level || ''] || lcat.education.level || 'Not specified',
                                                                lcat.education.field && ` in ${lcat.education.field}`
                                                            ]
                                                        }),
                                                        lcat.education.substitution && /*#__PURE__*/ _jsxs("p", {
                                                            className: "mt-0.5 text-xs text-gray-500",
                                                            children: [
                                                                "Substitution: ",
                                                                lcat.education.substitution
                                                            ]
                                                        })
                                                    ]
                                                }),
                                                lcat.experience && /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Experience"
                                                        }),
                                                        /*#__PURE__*/ _jsxs("p", {
                                                            className: "mt-1 text-sm text-gray-900",
                                                            children: [
                                                                lcat.experience.minimumYears != null ? `${lcat.experience.minimumYears}+ years` : 'Not specified',
                                                                lcat.experience.type && ` (${lcat.experience.type})`
                                                            ]
                                                        }),
                                                        lcat.experience.description && /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-0.5 text-xs text-gray-500",
                                                            children: lcat.experience.description
                                                        })
                                                    ]
                                                }),
                                                lcat.clearance && lcat.clearance !== 'none' && /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Clearance"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1",
                                                            children: /*#__PURE__*/ _jsxs(Badge, {
                                                                className: clearanceColors[lcat.clearance] || 'bg-gray-100 text-gray-700',
                                                                variant: "secondary",
                                                                children: [
                                                                    /*#__PURE__*/ _jsx(Shield, {
                                                                        className: "w-3 h-3 mr-1"
                                                                    }),
                                                                    clearanceLabels[lcat.clearance] || lcat.clearance
                                                                ]
                                                            })
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        lcat.certifications && lcat.certifications.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx(Label, {
                                                    children: "Certifications"
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "mt-1 flex flex-wrap gap-1",
                                                    children: lcat.certifications.map((cert, i)=>/*#__PURE__*/ _jsx(Badge, {
                                                            variant: "outline",
                                                            className: "text-xs",
                                                            children: cert
                                                        }, i))
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-4 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Requirements & Capacity"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "grid grid-cols-2 gap-4",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            htmlFor: "level",
                                                            children: "Level"
                                                        }),
                                                        isEditMode ? /*#__PURE__*/ _jsx(Input, {
                                                            id: "level",
                                                            value: formData.level || '',
                                                            onChange: (e)=>setFormData({
                                                                    ...formData,
                                                                    level: e.target.value
                                                                }),
                                                            placeholder: "e.g., Senior, Mid, Junior",
                                                            className: "mt-1"
                                                        }) : /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-sm text-gray-900",
                                                            children: lcat.level || 'Not specified'
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            htmlFor: "estimatedHours",
                                                            children: "Estimated Hours"
                                                        }),
                                                        isEditMode ? /*#__PURE__*/ _jsx(Input, {
                                                            id: "estimatedHours",
                                                            type: "number",
                                                            value: formData.estimatedHours || '',
                                                            onChange: (e)=>setFormData({
                                                                    ...formData,
                                                                    estimatedHours: Number.parseFloat(e.target.value) || undefined
                                                                }),
                                                            placeholder: "Total hours",
                                                            className: "mt-1"
                                                        }) : /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-sm text-gray-900",
                                                            children: lcat.estimatedHours ? lcat.estimatedHours.toLocaleString() : 'Not specified'
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-4 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Pricing"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "grid grid-cols-2 gap-4",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Base Hourly Rate"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-sm text-gray-900",
                                                            "data-testid": Testids.lcat.rateDisplay,
                                                            children: lcat.baseHourlyRate ? `$${lcat.baseHourlyRate.toLocaleString(undefined, {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2
                                                            })}/hr` : 'Not specified'
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-xs text-muted-foreground",
                                                            children: "Rates are set in Pricing."
                                                        })
                                                    ]
                                                }),
                                                lcat.ceilingRate != null && lcat.ceilingRate > 0 && /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Ceiling Rate"
                                                        }),
                                                        /*#__PURE__*/ _jsxs("p", {
                                                            className: "mt-1 text-sm text-gray-900",
                                                            children: [
                                                                "$",
                                                                lcat.ceilingRate.toLocaleString(undefined, {
                                                                    minimumFractionDigits: 2,
                                                                    maximumFractionDigits: 2
                                                                }),
                                                                "/hr"
                                                            ]
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-4 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Source Information"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "grid grid-cols-2 gap-4 text-sm",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Document"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-gray-700",
                                                            children: lcat.source?.filename || 'Not available'
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Section"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-gray-700",
                                                            children: lcat.source?.sectionNumber || 'Not available'
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Page"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-gray-700",
                                                            children: lcat.source?.pageNumber != null ? lcat.source.pageNumber : 'Not available'
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Confidence Score"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1",
                                                            children: getConfidenceBadge(lcat.confidence)
                                                        })
                                                    ]
                                                }),
                                                lcat.extractedAt && /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Extracted At"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-gray-700",
                                                            children: new Date(lcat.extractedAt).toLocaleString()
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        Boolean(hasSourceDoc) && /*#__PURE__*/ _jsxs(Button, {
                                            variant: "outline",
                                            className: "w-full mt-4",
                                            onClick: ()=>setIsDocViewerOpen(true),
                                            disabled: !sourceDocument,
                                            children: [
                                                /*#__PURE__*/ _jsx(FileText, {
                                                    className: "w-4 h-4 mr-2"
                                                }),
                                                sourceDocument ? 'View Source in Document' : 'Source Document Not Available'
                                            ]
                                        })
                                    ]
                                }),
                                history.hasHistory && /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-3 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Amendment Versions"
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "-mt-2 text-xs text-slate-500",
                                            children: [
                                                "This role appears in ",
                                                history.versions.length,
                                                " versions across base and amendments. The current version is highlighted."
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(AmendmentTimeline, {
                                            entries: timelineEntries,
                                            title: "Version history"
                                        })
                                    ]
                                }),
                                ((lcat.pwsReferences?.length ?? 0) > 0 || (lcat.attachmentReferences?.length ?? 0) > 0) && /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-3 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Source References"
                                        }),
                                        lcat.pwsReferences && lcat.pwsReferences.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx(Label, {
                                                    children: "PWS Sections"
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "mt-1 flex flex-wrap gap-1",
                                                    children: lcat.pwsReferences.map((ref)=>/*#__PURE__*/ _jsxs(Badge, {
                                                            variant: "outline",
                                                            className: "text-xs bg-purple-50 text-purple-700 border-purple-200",
                                                            children: [
                                                                "PWS ",
                                                                ref
                                                            ]
                                                        }, ref))
                                                })
                                            ]
                                        }),
                                        lcat.attachmentReferences && lcat.attachmentReferences.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx(Label, {
                                                    children: "Attachments"
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "mt-1 flex flex-wrap gap-1",
                                                    children: lcat.attachmentReferences.map((ref)=>/*#__PURE__*/ _jsx(Badge, {
                                                            variant: "outline",
                                                            className: "text-xs bg-amber-50 text-amber-700 border-amber-200",
                                                            children: ref
                                                        }, ref))
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                lcat.notes && /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-2 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Notes"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm text-gray-700 whitespace-pre-wrap",
                                            children: lcat.notes
                                        })
                                    ]
                                }),
                                lcat.tags && lcat.tags.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-2 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Tags"
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "flex flex-wrap gap-1",
                                            children: lcat.tags.map((tag, i)=>/*#__PURE__*/ _jsx(Badge, {
                                                    variant: "secondary",
                                                    className: "text-xs",
                                                    children: tag
                                                }, i))
                                        })
                                    ]
                                }),
                                lcat.metadata && Object.keys(lcat.metadata).length > 0 && /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-4 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Additional Metadata"
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "bg-gray-50 rounded p-3 text-xs font-mono",
                                            children: /*#__PURE__*/ _jsx("pre", {
                                                className: "whitespace-pre-wrap",
                                                children: JSON.stringify(lcat.metadata, null, 2)
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "border-t pt-4",
                                    children: /*#__PURE__*/ _jsx(EntityAuditTrail, {
                                        projectId: projectId,
                                        resourceType: "lcat",
                                        resourceId: lcat.code
                                    })
                                })
                            ]
                        })
                    })
                ]
            }),
            sourceDocument && /*#__PURE__*/ _jsx(DocumentViewerDrawer, {
                isOpen: isDocViewerOpen,
                onClose: ()=>setIsDocViewerOpen(false),
                projectId: projectId,
                document: sourceDocument,
                highlightBlockId: lcat.source?.blockId || lcat.metadata?.sourceBlockId,
                initialPage: lcat.source?.pageNumber,
                mode: "extension"
            })
        ]
    }), portalContainer);
});
// PR-C.4 helpers — render LCAT version rows for the AmendmentTimeline.
// Each timeline row has a single primary line and an optional secondary
// line. We pick the fields that actually drift across amendments (rate
// ceiling, level, clearance, key personnel flag) so the timeline is
// scannable rather than showing repeated boilerplate.
function buildLcatPrimary(row) {
    // Prefer level + title when level is set so seniority drift is visible
    // ("Senior Software Engineer" vs "Software Engineer"). Otherwise just
    // the title.
    const title = (row.title ?? '').trim() || 'Untitled role';
    const level = (row.level ?? '').trim();
    if (level && !title.toLowerCase().includes(level.toLowerCase())) {
        return `${level} ${title}`;
    }
    return title;
}
function buildLcatSecondary(row) {
    // Surface the fields most likely to drift across amendments. Each
    // included field is rendered as a small inline metadata chip; the
    // overall string stays under ~80 characters so the timeline row
    // doesn't wrap.
    const parts = [];
    if (row.ceilingRate != null && row.ceilingRate > 0) {
        parts.push(`ceiling $${row.ceilingRate}/hr`);
    }
    if (row.baseHourlyRate != null && row.baseHourlyRate > 0) {
        parts.push(`base $${row.baseHourlyRate}/hr`);
    }
    if (row.experience?.minimumYears && row.experience.minimumYears > 0) {
        parts.push(`${row.experience.minimumYears}+ yrs`);
    }
    if (row.clearance) {
        parts.push(row.clearance);
    }
    if (row.isKeyPersonnel) {
        parts.push('key personnel');
    }
    return parts.length > 0 ? parts.join(' · ') : undefined;
}
